<script>
	import { preventDefault } from "svelte/legacy";
	import { fade } from "svelte/transition";

	import { modalState, hideModal } from "@app/store/modalStore.svelte.js";
	import { styleState } from "@app/store/styleStore.svelte.js";
	import { insertHistoryAction } from "@app/store/historyStore.svelte.js";
	import { eventGroupState } from "@app/store/eventGroupStore.svelte.js";
	import { projectState } from "@app/store/projectStore.svelte.js";
	import { toast } from "@zerodevx/svelte-toast";

	/* CC LIB */
	import defaults from "@cclib/lib/defaults.js";
	import encodings from "@cclib/dict/encodings.js";
	import formats from "@cclib/lib/formats.js";
	import frameRates from "@cclib/dict/frameRates.js";
	import _EventGroup from "@cclib/classes/eventGroup.js";
	import tcLib from "@cclib/lib/timecode.js";

	import LanguageSelect from "@app/components/inputs/LanguageSelect.svelte";
	import Swal from "sweetalert2";
	import { SWAL_BASE, SWAL_CLASSES } from "@app/lib/swalClasses.js";
	import * as m from "@app/paraglide/messages.js";

	import mergeTranslation from "@app/lib/mergeTranslation.js";
	import {
		getProjectClockIncode,
		isDropFrameEnabled,
		getUseClockTimeDefault,
		cloneDecodeOptions,
		readSubtitleFileContents,
		buildDecodeOptions,
		decodeSubtitleSource,
		detectAutomaticOffset,
		applyAutomaticOffset,
		stripBlankEvents,
		hasOverlaps,
		applyOverlapFix,
		importSpeakersFromGroup,
		applyDropFrameConvert,
	} from "@app/lib/subtitleDecodePipeline.js";

	let eventGroupDefaults = JSON.parse(localStorage.getItem("cc-event-group-defaults")) || {};
	let translationImportDefaults = JSON.parse(localStorage.getItem("cc-translation-import-defaults")) || {};

	const allowedExtensions = "." + formats.srcExt.join(", .");

	function createFileState(role, paneDefaults = {}) {
		return {
			role: role,
			files: undefined,
			srcExt: undefined,
			srcFileName: "",
			srcProfile: undefined,
			srcFrameRate: projectState.frameRate,
			srcDropFrame: projectState.dropFrame ? "true" : "false",
			srcIncode: paneDefaults.srcIncode || getProjectClockIncode(),
			srcUseClockTime: paneDefaults.srcUseClockTime || "yes",
			fileEncoding: defaults.encoding,
			dropFrameConvert: false,
			showOptions: translationImportDefaults.showOptions || false,
			fileContents: "",
			formatOptions: [],
		};
	}

	let source = $state(createFileState("source", translationImportDefaults.source));
	let target = $state(createFileState("target", translationImportDefaults.target));

	let displayName = $state(m.translation_merge_default_name()),
		displayNameEdited = $state(false),
		sourceLanguage = $state(eventGroupDefaults.language || "en-US"),
		language = $state(eventGroupDefaults.language || "en-US"),
		rtl = $state(eventGroupDefaults.rtl),
		originalRtl = $state(false),
		working = $state(false);

	let readyToImport = $derived(!!source.fileContents && !!target.fileContents && !working);

	// Handler for file inputs
	async function handleFileInput(pane, event) {
		pane.files = Array.from(event.currentTarget.files || []);
		await updateFile(pane);
	}

	async function updateFile(pane) {
		if (pane.files && pane.files[0] && pane.files[0].name) {
			let fileName = pane.files[0].name.split(".");
			pane.srcExt = fileName.pop().toLowerCase();
			pane.srcFileName = fileName.join(".");

			if (!formats.srcProfiles[pane.srcExt]) {
				toast.push(m.translation_import_unsupported_format_toast({ extension: pane.srcExt }), {
					classes: ["toast-danger"],
				});
				return;
			}

			pane.srcProfile = formats.srcProfiles[pane.srcExt][0];
			pane.formatOptions = cloneDecodeOptions(pane.srcProfile);
			pane.srcUseClockTime = getUseClockTimeDefault(pane.srcProfile);

			if (pane.role === "target" && !displayNameEdited) {
				displayName = pane.srcFileName || m.translation_merge_default_name();
			}

			await updateFileContents(pane);
		}
	}

	function updateProfile(pane) {
		pane.formatOptions = cloneDecodeOptions(pane.srcProfile);
		pane.srcUseClockTime = getUseClockTimeDefault(pane.srcProfile);
		updateFileContents(pane);
	}

	async function updateFileContents(pane) {
		if (pane.files && pane.files[0]) {
			toast.push(m.subtitle_import_reading_file_toast(), {
				classes: ["toast-warning"],
			});

			try {
				pane.fileContents = await readSubtitleFileContents(pane.files[0], {
					srcExt: pane.srcExt,
					srcProfile: pane.srcProfile,
					fileEncoding: pane.fileEncoding,
				});
			} catch (err) {
				console.log(err, err.message);
				toast.push(m.translation_import_failed_toast({ error: err.message }), {
					classes: ["toast-danger"],
				});
			}
		}
	}

	function validatePaneIncode(pane, e) {
		pane.srcIncode = tcLib.tcValidate(e.target.value, parseFloat(pane.srcFrameRate), isDropFrameEnabled(pane.srcDropFrame));
	}

	async function decodePane(pane) {
		pane.srcIncode = tcLib.tcValidate(pane.srcIncode, parseFloat(pane.srcFrameRate), isDropFrameEnabled(pane.srcDropFrame));

		let options = buildDecodeOptions({
			srcProfile: pane.srcProfile,
			srcFrameRate: pane.srcFrameRate,
			srcDropFrame: pane.srcDropFrame,
			srcIncode: pane.srcIncode,
			srcUseClockTime: pane.srcUseClockTime,
			formatOptions: pane.formatOptions,
			window: {
				width: styleState.width,
				height: styleState.height,
				xPadding: styleState.xPadding,
				yPadding: styleState.yPadding,
			},
		});

		return await decodeSubtitleSource(pane.fileContents, options);
	}

	function getOffsetAmount(eventGroup) {
		return eventGroup.events.length > 0 ? detectAutomaticOffset(eventGroup) : 0;
	}

	async function importTranslationFiles() {
		if (working) {
			return;
		}

		working = true;

		toast.push(m.translation_import_importing_toast(), {
			classes: ["toast-warning"],
		});

		try {
			let sourceEvGroup = await decodePane(source);
			let targetEvGroup = await decodePane(target);

			/* Automatic offset. Asked once and applied to both files. */
			let sourceOffset = getOffsetAmount(sourceEvGroup);
			let targetOffset = getOffsetAmount(targetEvGroup);
			let offsetAmount = Math.max(sourceOffset, targetOffset);

			if (offsetAmount != 0) {
				try {
					if (projectState.incode > 0) {
						sourceEvGroup = applyAutomaticOffset(sourceEvGroup);
						targetEvGroup = applyAutomaticOffset(targetEvGroup);
					} else {
						let offsetRes = await Swal.fire({
							...SWAL_BASE,
							customClass: SWAL_CLASSES.confirmCancelLight,
							title: m.subtitle_import_auto_offset_title(),
							text: m.subtitle_import_auto_offset_text({
								timecode: tcLib.secToTcClock(offsetAmount, projectState.frameRate, projectState.dropFrame),
							}),
							icon: "question",
							showCancelButton: true,
							confirmButtonText: m.yes(),
							cancelButtonText: m.no(),
							allowOutsideClick: false,
							allowEscapeKey: false,
						});

						if (offsetRes.isConfirmed) {
							projectState.incode = offsetAmount;
							sourceEvGroup = applyAutomaticOffset(sourceEvGroup);
							targetEvGroup = applyAutomaticOffset(targetEvGroup);
						}
					}
				} catch (err) {
					console.log(err, err.message);
				}
			}

			sourceEvGroup = stripBlankEvents(sourceEvGroup);
			targetEvGroup = stripBlankEvents(targetEvGroup);

			/* Overlapping events. Asked once and applied to both files. */
			if (hasOverlaps(sourceEvGroup) || hasOverlaps(targetEvGroup)) {
				let overlapRes = await Swal.fire({
					...SWAL_BASE,
					customClass: SWAL_CLASSES.confirmCancelLight,
					title: m.subtitle_import_overlap_title(),
					text: m.subtitle_import_overlap_text(),
					icon: "question",
					showCancelButton: true,
					confirmButtonText: m.yes(),
					cancelButtonText: m.no(),
					allowOutsideClick: false,
					allowEscapeKey: false,
				});

				if (overlapRes.isConfirmed) {
					sourceEvGroup = applyOverlapFix(sourceEvGroup);
					targetEvGroup = applyOverlapFix(targetEvGroup);
				}
			}

			//Import Speakers from events
			importSpeakersFromGroup(sourceEvGroup);
			importSpeakersFromGroup(targetEvGroup);

			if (source.dropFrameConvert) {
				sourceEvGroup = applyDropFrameConvert(sourceEvGroup);
			}

			if (target.dropFrameConvert) {
				targetEvGroup = applyDropFrameConvert(targetEvGroup);
			}

			/* Run the same merge used by Event Group Options > Translation Merge */
			let mergedEvents = mergeTranslation(sourceEvGroup, targetEvGroup);

			const sourceGroup = {...(new _EventGroup({
				type: "subtitle",
				name: source.srcFileName,
				language: sourceLanguage,
				rtl: originalRtl,
				maxLines: eventGroupDefaults.maxLines || 2,
				maxChars: eventGroupDefaults.maxChars || 32,
				minDuration: eventGroupDefaults.minDuration || 0.5,
				maxDuration: eventGroupDefaults.maxDuration || 12,
				overlap: eventGroupDefaults.overlap,
				fontFamily: eventGroupDefaults.fontFamily ?? "plexMono",
				fontSize: eventGroupDefaults.fontSize || 16,
				events: sourceEvGroup.events,
			}))};
			eventGroupState.push(sourceGroup);

			eventGroupState.push(
				{...(new _EventGroup({
					type: "translation",
					name: displayName,
					language: language,
					rtl: rtl,
					linkedGroup: sourceGroup.id,
					originalRtl: originalRtl,
					originalFontFamily: eventGroupDefaults.fontFamily ?? "arial",
					fontFamily: eventGroupDefaults.fontFamily ?? "plexMono",
					fontSize: eventGroupDefaults.fontSize || 16,
					maxLines: eventGroupDefaults.maxLines || 2,
					maxChars: eventGroupDefaults.maxChars || 32,
					minDuration: eventGroupDefaults.minDuration || 0.5,
					maxDuration: eventGroupDefaults.maxDuration || 12,
					overlap: eventGroupDefaults.overlap,
					events: mergedEvents,
				}))}
			);

			projectState.selected = eventGroupState.length - 1;
			eventGroupState[projectState.selected].selected = [0];

			insertHistoryAction({
				name: "import translation", //action name
				eventGroup: projectState.selected,
				snapshots: [
					{
						store: "eventGroupState",
						value: JSON.stringify(eventGroupState),
					},
				],
			});

			localStorage.setItem(
				"cc-translation-import-defaults",
				JSON.stringify({
					showOptions: source.showOptions || target.showOptions,
					source: {
						srcIncode: source.srcIncode,
						srcUseClockTime: source.srcUseClockTime,
					},
					target: {
						srcIncode: target.srcIncode,
						srcUseClockTime: target.srcUseClockTime,
					},
				})
			);

			toast.push(m.translation_import_success_toast(), {
				classes: ["toast-success"],
			});

			hideModal();
		} catch (err) {
			console.log(err, err.message);
			toast.push(m.translation_import_failed_toast({ error: err.message }), {
				classes: ["toast-danger"],
			});
		} finally {
			working = false;
		}
	}
</script>

{#snippet filePane(pane, idPrefix, paneLabel, fileLabel)}
	<div class="mb-3 shadow bg-secondary rounded p-3">
		<h5 class="lead text-warning">{paneLabel}</h5>
		<label class="form-label" id="{idPrefix}FileInputLabel" for="{idPrefix}FilePicker">
			{fileLabel}
			<i class="bi bi-info-circle" aria-label={m.subtitle_import_supported_formats_label({ formats: allowedExtensions })}></i>
		</label>
		<input
			type="file"
			class="form-control"
			id="{idPrefix}FilePicker"
			onchange={(event) => handleFileInput(pane, event)}
			accept={allowedExtensions}
			aria-labelledby="{idPrefix}FileInputLabel" />
	</div>

	<!-- Source Profile List -->
	{#if formats.srcProfiles[pane.srcExt] && formats.srcProfiles[pane.srcExt].length > 1}
		<div class="mb-3">
			<label class="form-label" for="{idPrefix}ProfileSelectList">
				{m.subtitle_import_source_profile_label()} <i class="bi bi-asterisk text-danger"></i>
			</label>
			<select id="{idPrefix}ProfileSelectList" class="form-select" bind:value={pane.srcProfile} onchange={() => updateProfile(pane)}>
				{#each formats.srcProfiles[pane.srcExt] as profile (profile)}
					<option>{profile}</option>
				{/each}
			</select>
		</div>
	{/if}

	<button
		type="button"
		id="{idPrefix}MoreOptionsBtn"
		class="btn btn-outline-warning mb-2 dropdown-toggle"
		onclick={() => {
			pane.showOptions = !pane.showOptions;
		}}>{m.more_options()}</button>

	{#if pane.showOptions}
		<div class="advancedOptions p-3 bg-secondary shadow rounded">
			<div class="row pt-2 mb-2">
				<h5 class="col-12 lead text-warning">{m.subtitle_import_general_options()}</h5>
			</div>
			<div class="row mb-2">
				<div class="mb-3 col-8">
					<label class="form-label" for="{idPrefix}Incode">{m.subtitle_export_incode_label()}</label>
					<input
						type="text"
						class="form-control"
						id="{idPrefix}Incode"
						bind:value={pane.srcIncode}
						onblur={(e) => validatePaneIncode(pane, e)}
						onfocus={(e) => {
							e.target.select();
						}} />
				</div>
				<div class="mb-3 col-4">
					<label class="form-label" for="{idPrefix}UseClockTime">{m.subtitle_export_use_clock_time()}</label>
					<select id="{idPrefix}UseClockTime" class="form-select" bind:value={pane.srcUseClockTime}>
						<option value="yes">{m.subtitle_export_yes()}</option>
						<option value="no">{m.subtitle_export_no()}</option>
					</select>
				</div>
				<div class="col-8">
					<label class="form-label" for="{idPrefix}FrameRateSelect">{m.subtitle_import_source_frame_rate_label()}</label>
					<select id="{idPrefix}FrameRateSelect" class="form-select" bind:value={pane.srcFrameRate}>
						{#each frameRates.frameRates as frameRate (frameRate)}
							<option>{frameRate}</option>
						{/each}
					</select>
				</div>
				{#if parseFloat(pane.srcFrameRate) === 29.97 || parseFloat(pane.srcFrameRate) === 59.94}
					<div class="col-4">
						<label class="form-label" for="{idPrefix}DropFrameSelect">{m.subtitle_import_drop_frame_label()}</label>
						<select id="{idPrefix}DropFrameSelect" class="form-select" bind:value={pane.srcDropFrame}>
							<option value="true">{m.common_true()}</option>
							<option value="false">{m.common_false()}</option>
						</select>
					</div>
				{/if}
			</div>
			<div class="mb-3 mt-2">
				<label class="form-label" for="{idPrefix}FileEncoding">{m.subtitle_import_file_encoding_label()}</label>
				<select id="{idPrefix}FileEncoding" class="form-select" bind:value={pane.fileEncoding} onchange={() => updateFileContents(pane)}>
					{#each encodings as encodingOption (encodingOption)}
						<option>{encodingOption}</option>
					{/each}
				</select>
			</div>
			<div class="form-check">
				<input class="form-check-input" type="checkbox" id="{idPrefix}DropFrameConvert" bind:checked={pane.dropFrameConvert} />
				<label class="form-check-label" for="{idPrefix}DropFrameConvert">
					{m.subtitle_import_drop_frame_convert_label()}
					<i class="bi bi-info-circle" title={m.subtitle_import_drop_frame_convert_help()}></i>
				</label>
			</div>
			{#if pane.formatOptions.length > 0}
				<div class="row pt-4 mt-4 mb-3 border-top border-secondary">
					<h5 class="col-12 lead text-warning">{m.subtitle_import_decoding_options_label()}</h5>
					{#each pane.formatOptions as decodeOption (decodeOption.name)}
						<div class="mb-3 col-6">
							<label class="form-label" for="{idPrefix}DecodeOption">{decodeOption.name}</label>
							{#if decodeOption.type === "textarea"}
								<textarea class="form-control" rows="3" bind:value={decodeOption.selected}></textarea>
							{:else if decodeOption.type === "text-input"}
								<input class="form-control" type="text" bind:value={decodeOption.selected} />
							{:else if decodeOption.type === "number-input"}
								<input class="form-control" type="number" bind:value={decodeOption.selected} />
							{:else}
								<select class="form-select" bind:value={decodeOption.selected}>
									{#each decodeOption.values as valueOption (valueOption)}
										<option>{valueOption}</option>
									{/each}
								</select>
							{/if}
						</div>
					{/each}
				</div>
			{/if}
		</div>
	{/if}

	<!-- File Preview -->
	<div class="mb-3 mt-3">
		<label class="form-label" for="{idPrefix}PreviewText">{m.subtitle_import_source_preview_label()}</label>
		<textarea
			bind:value={pane.fileContents}
			disabled
			class="form-control"
			id="{idPrefix}PreviewText"
			rows="8"
			placeholder={m.subtitle_import_source_preview_placeholder()}></textarea>
	</div>
{/snippet}

<div
	transition:fade={{ duration: 100 }}
	class="modal {modalState.selected === 'translationImport' ? 'show d-block' : ''}"
	role="dialog"
	aria-labelledby="translationImportTitle"
	aria-modal="true"
	tabindex="-1"
	id="TranslationImportModal">
	<div class="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable" role="document">
		<div class="modal-content">
			<div class="modal-header">
				<h4 class="modal-title" id="translationImportTitle">
					{m.translation_import_title()}
				</h4>
				<button type="button" class="btn-close" id="btnCloseTranslationImport" aria-label={m.close()} onclick={hideModal}></button>
			</div>
			<div class="modal-body">
				<p class="small text-muted"><i class="bi bi-lightning-fill"></i> {m.translation_import_help()}</p>

				<form onsubmit={preventDefault(importTranslationFiles)} aria-label={m.translation_import_form_label()}>
					<!-- Shared Event Group Settings -->
					<div class="row">
						<div class="mb-3 col-12 col-md-4">
							<label class="form-label" for="translationImportDisplayName">
								<span class="badge bg-primary">{m.new()}</span>
								{m.translation_merge_display_name_label()}
							</label>
							<input
								class="form-control"
								id="translationImportDisplayName"
								required
								placeholder={m.translation_merge_display_name_placeholder()}
								type="text"
								bind:value={displayName}
								oninput={() => (displayNameEdited = true)}
								aria-required="true" />
						</div>
						<div class="mb-3 col-12 col-md-4">
							<label class="form-label" for="translationImportSourceLanguage">{m.source_language()}</label>
							<LanguageSelect id="translationImportSourceLanguage" class="form-control" bind:value={sourceLanguage} />
						</div>
						<div class="mb-3 col-12 col-md-4">
							<label class="form-label" for="translationImportLanguage">{m.target_language()}</label>
							<LanguageSelect id="translationImportLanguage" class="form-control" bind:value={language} />
						</div>
						<div class="mb-3 col-12 col-md-4">
							<div class="form-check form-switch mb-2">
								<input type="checkbox" class="form-check-input" id="translationImportRtl" bind:checked={rtl} aria-checked={rtl} />
								<label class="form-check-label" for="translationImportRtl">{m.right_to_left()}</label>
							</div>
							<div class="form-check form-switch mb-2">
								<input
									type="checkbox"
									class="form-check-input"
									id="translationImportOriginalRtl"
									bind:checked={originalRtl}
									aria-checked={originalRtl} />
								<label class="form-check-label" for="translationImportOriginalRtl">{m.translation_import_source_rtl_label()}</label>
							</div>
						</div>
					</div>

					<!-- Source / Target File Panes -->
					<div class="row border-top border-secondary pt-3">
						<div class="col-12 col-lg-6">
							{@render filePane(
								source,
								"translationImportSource",
								m.translation_import_source_pane_label(),
								m.translation_import_select_source_file_label()
							)}
						</div>
						<div class="col-12 col-lg-6">
							{@render filePane(
								target,
								"translationImportTarget",
								m.translation_import_target_pane_label(),
								m.translation_import_select_target_file_label()
							)}
						</div>
					</div>
				</form>
			</div>
			<div class="modal-footer">
				<button
					class="btn btn-primary"
					type="button"
					id="btnTranslationImport"
					onclick={importTranslationFiles}
					disabled={!readyToImport}
					aria-label={m.translation_import_action_label()}>{m.translation_import_action_label()}</button>
			</div>
		</div>
	</div>
</div>

<style lang="css">
	.advancedOptions {
		overflow-y: auto;
		overflow-x: hidden;
		max-height: 40vh;
	}
</style>
